"use client";

/**
 * F37 (Sprint 16) — Sparrow speech bubbles for the landing hero only.
 *
 * Renders next to the floating Sparrow mascot in the /spending-personality-test
 * hero (and NOWHERE else — the quiz and result pages deliberately do not import
 * this). Two comic-style bubbles fade in one after another, then stay static;
 * there is no looping rotation.
 *
 * Accessibility / motion:
 *  - `prefers-reduced-motion: reduce` → both bubbles render immediately with no
 *    fade animation (a single calm, static state). The copy is identical so a
 *    reduced-motion user is not shown less information.
 *  - Bubbles are real text (not aria-hidden) so screen readers read them in
 *    order; the tail is a decorative CSS triangle marked aria-hidden.
 *
 * Analytics: fires `sparrow_hero_bubble_visible` ONCE, the first time the block
 * scrolls into view (IntersectionObserver, mirrors the ArchetypeGrid / hero
 * beacon pattern). sessionStorage debounce avoids a double-count on a
 * back-button reload within the same session.
 *
 * Spec: docs/09-adr/backlog.md (F37)
 */

import { useEffect, useRef, useState } from "react";

import { track } from "@/lib/analytics";

const BUBBLES = [
  "Hi — I'm Sparrow. I help people see where their money quietly slips away.",
  "Take 5 minutes. I'll show you yours.",
] as const;

/** Delay before the second bubble fades in (motion users only). */
const SECOND_BUBBLE_DELAY_MS = 2_400;

const SESSION_KEY = "mi:f37:sparrow-bubble";

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    setReduced(mq.matches);
    const onChange = (e: MediaQueryListEvent) => setReduced(e.matches);
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  return reduced;
}

export function SparrowHeroBubble() {
  const reduceMotion = usePrefersReducedMotion();
  // How many bubbles are currently shown. Reduced-motion users get both at
  // once; everyone else sees the first immediately, the second after a delay.
  const [shown, setShown] = useState(1);
  const rootRef = useRef<HTMLDivElement>(null);
  const firedRef = useRef(false);

  useEffect(() => {
    if (reduceMotion) {
      setShown(BUBBLES.length);
      return;
    }
    const id = window.setTimeout(() => setShown(BUBBLES.length), SECOND_BUBBLE_DELAY_MS);
    return () => window.clearTimeout(id);
  }, [reduceMotion]);

  // One-shot visibility beacon.
  useEffect(() => {
    const node = rootRef.current;
    if (!node || typeof IntersectionObserver === "undefined") return;

    let alreadyFired = false;
    try {
      alreadyFired = window.sessionStorage.getItem(SESSION_KEY) === "1";
    } catch {
      alreadyFired = false;
    }
    if (alreadyFired) {
      firedRef.current = true;
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting && !firedRef.current) {
            firedRef.current = true;
            track("sparrow_hero_bubble_visible");
            try {
              window.sessionStorage.setItem(SESSION_KEY, "1");
            } catch {
              // ignored — accept rare double-counts over a noisy console.
            }
            observer.disconnect();
          }
        }
      },
      { threshold: 0.4 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={rootRef}
      className="pointer-events-none absolute -bottom-2 left-1/2 z-10 w-[min(20rem,88%)] -translate-x-1/2 space-y-2 sm:left-auto sm:right-0 sm:translate-x-0 md:-bottom-4"
    >
      {BUBBLES.map((text, i) => {
        const visible = i < shown;
        return (
          <p
            key={i}
            className={`s5-sparrow-bubble ${
              reduceMotion ? "" : "s5-sparrow-bubble--anim"
            } ${visible ? "is-visible" : ""}`}
          >
            {text}
            <span className="s5-sparrow-bubble__tail" aria-hidden="true" />
          </p>
        );
      })}
    </div>
  );
}
